'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@7f-ui/components/button/button';
import { Price } from '@7f-ui/components/price';
import type { GetRecommendationFragment } from '@data-access/lib/gql/cart/documents';
import { getMapUrl } from '@feat/feat-map/urls';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { circleToPolygon, ProductType } from '@utils/helpers';
import { formatIsoDate } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import Link from 'next/link';
import { AddedToCartItemDetails, AddedToCartItemImage } from './added-to-cart-item-details';
type AddedToCartRecommendationProps = {
  recommendation: GetRecommendationFragment | null | undefined;
  startDate: string | null | undefined;
  endDate?: string | null;
};
export function AddedToCartRecommendation(t0) {
  const $ = _c(21);
  const {
    recommendation,
    startDate,
    endDate
  } = t0;
  let t1;
  if ($[0] !== recommendation) {
    t1 = recommendation ?? {};
    $[0] = recommendation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    nexperiences,
    destination
  } = t1;
  let t2;
  if ($[2] !== startDate) {
    t2 = startDate && {
      from: formatIsoDate(startDate)
    };
    $[2] = startDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== endDate) {
    t3 = endDate && {
      to: formatIsoDate(endDate)
    };
    $[4] = endDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = new URLSearchParams({
      ...t2,
      ...t3
    });
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const searchParams = t4;
  const experience = nexperiences?.[0];
  const showLinkToSearchPage = destination && (experience || destination.accommodationCount > 1 && destination.beachChairStatus === "beachChairsAvailable");
  if (!experience && !showLinkToSearchPage) {
    return null;
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <hr className="my-8 border-t border-t-primary/15" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="pb-4 font-body-copy">Möchtest du noch mehr in der Nähe entdecken?</p>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== experience || $[12] !== searchParams) {
    t7 = experience && <a href={`/erlebnisse/${experience.publicId}/${experience.slug}/?${searchParams}`} className="flex gap-x-4"><AddedToCartItemDetails title={experience.title} subTitle={<span>{experience.hasMultiplePrices ? "ab " : null}<Price amount={experience?.price} currency={experience.currency} />/{" "}{experience.priceCategory}</span>} image={<AddedToCartItemImage image={{
        url: experience?.images?.[0]?.url,
        alt: experience.title
      }} />} /></a>;
    $[11] = experience;
    $[12] = searchParams;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== recommendation || $[15] !== searchParams || $[16] !== showLinkToSearchPage) {
    t8 = showLinkToSearchPage && <ShowMoreButton recommendation={recommendation} searchParams={searchParams} />;
    $[14] = recommendation;
    $[15] = searchParams;
    $[16] = showLinkToSearchPage;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <>{t5}<div className="grid justify-items-start">{t6}{t7}{t8}</div></>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
const ShowMoreButton = t0 => {
  const $ = _c(10);
  const {
    recommendation,
    searchParams
  } = t0;
  let t1;
  if ($[0] !== recommendation) {
    t1 = recommendation ?? {};
    $[0] = recommendation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    lat,
    lng,
    location
  } = t1;
  if (!lat || !lng || !location) {
    return null;
  }
  let t2;
  if ($[2] !== lat || $[3] !== lng || $[4] !== location || $[5] !== searchParams) {
    t2 = getMapUrl({
      polygon: circleToPolygon({
        lat,
        lng
      }, 10),
      productType: ProductType.EXPERIENCE,
      location,
      ...Object.fromEntries(searchParams)
    });
    $[2] = lat;
    $[3] = lng;
    $[4] = location;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const href = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = shopTestId("AddedToCart.view-more");
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== href) {
    t4 = <Button size="small" as={Link} href={href} data-testid={t3} variant="underline" onClick={_temp}>Weitere anzeigen</Button>;
    $[8] = href;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
function _temp() {
  return trackClickCta("show more", {
    type: "pdp - basket overlay"
  });
}